import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { normalizeNamePart } from '../../../shared/model';
import {
  CheckboxIndicator,
  pressableControlLifecycleStyles,
  PrimaryButton,
} from '../../../shared/ui';

import { TipCheckoutPaymentMethods } from './tip-checkout-payment-methods';

type TipCheckoutPaymentSectionProps = {
  canPay: boolean;
  isConsentAccepted: boolean;
  isFeeIncluded: boolean;
  isSubmitting?: boolean;
  isStatusError?: boolean;
  onConsentChange: (nextValue: boolean) => void;
  onFeeIncludedChange: (nextValue: boolean) => void;
  onPay: () => void;
  recipientFirstName?: string | null;
  statusMessage?: string | null;
};

const Section = styled.section.attrs({
  'data-ui': 'tips-checkout-payment-section',
})`
  display: grid;
  justify-items: center;
`;

const PayButton = styled(PrimaryButton)`
  opacity: 1;

  &:disabled {
    opacity: 0.45;
  }
`;

const PaymentHint = styled.p.attrs({
  'data-ui': 'tips-checkout-payment-hint',
})`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: 1.25;
  text-align: center;
`;

const StatusMessage = styled.p.attrs({
  'data-ui': 'tips-checkout-payment-status',
})<{ $isError?: boolean }>`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(17)};
  text-align: center;
  color: ${({ $isError, theme }) =>
    $isError ? theme.colors.support.danger : theme.colors.text.subdued};
`;

const ConsentLabel = styled.label.attrs({
  'data-ui': 'tips-checkout-payment-consent',
})`
  ${pressableControlLifecycleStyles};
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const ConsentInput = styled.input`
  position: absolute;
  opacity: 0;
  width: ${({ theme }) => theme.scale.px(1)};
  height: ${({ theme }) => theme.scale.px(1)};
  margin: 0;
  pointer-events: none;
`;

const ConsentText = styled.span`
  max-width: ${({ theme }) => theme.scale.px(210)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(17)};
  text-decoration: underline;
  text-decoration-thickness: ${({ theme }) => theme.scale.px(1)};
  user-select: none;
  -webkit-user-select: none;
`;

export function TipCheckoutPaymentSection({
  canPay,
  isConsentAccepted,
  isFeeIncluded,
  isSubmitting = false,
  isStatusError = false,
  onConsentChange,
  onFeeIncludedChange,
  onPay,
  recipientFirstName,
  statusMessage = null,
}: TipCheckoutPaymentSectionProps) {
  const { t } = useTranslation();
  const normalizedFirstName = normalizeNamePart(recipientFirstName ?? '');
  const feeNoticeText =
    normalizedFirstName.length > 0
      ? t('tipsCheckout.providerSelectionFeeNote', {
          name: normalizedFirstName,
        })
      : t('tipsCheckout.providerSelectionFeeNoteFallback');
  const statusMessageLines = statusMessage?.split('\n') ?? [];

  return (
    <Section>
      <PayButton
        disabled={!canPay || isSubmitting}
        type="button"
        onClick={onPay}
      >
        {t('tipsCheckout.payCta')}
      </PayButton>
      <PaymentHint>{t('tipsCheckout.paymentHint')}</PaymentHint>
      {statusMessage ? (
        <StatusMessage
          $isError={isStatusError}
          aria-live="polite"
          role={isStatusError ? 'alert' : 'status'}
        >
          {statusMessageLines.map((line, index) => (
            <Fragment key={`${index}:${line}`}>
              {index > 0 ? <br /> : null}
              {line}
            </Fragment>
          ))}
        </StatusMessage>
      ) : null}
      <TipCheckoutPaymentMethods />
      <ConsentLabel>
        <ConsentInput
          aria-label={t('login.consentPersonalData')}
          checked={isConsentAccepted}
          type="checkbox"
          onChange={event => onConsentChange(event.target.checked)}
        />
        <CheckboxIndicator checked={isConsentAccepted} variant="primary" />
        <ConsentText>{t('login.consentPersonalData')}</ConsentText>
      </ConsentLabel>
      <ConsentLabel>
        <ConsentInput
          aria-label={t('tipsCheckout.providerSelectionFeeToggleAria')}
          checked={isFeeIncluded}
          type="checkbox"
          onChange={event => onFeeIncludedChange(event.target.checked)}
        />
        <CheckboxIndicator checked={isFeeIncluded} variant="primary" />
        <ConsentText>{feeNoticeText}</ConsentText>
      </ConsentLabel>
    </Section>
  );
}
